/**
 * My Cards API client — fetches cards assigned to the authenticated user
 * across all boards, with cursor-based pagination.
 *
 * Requirements: 11.1, 11.2, 11.3, 11.5
 */

import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CardLabel {
  readonly labelId: string;
  readonly name: string;
  readonly color: string;
}

export interface MyCardItem {
  readonly cardId: string;
  readonly cardTitle: string;
  readonly boardId: string;
  readonly boardTitle: string;
  readonly listTitle: string;
  readonly dueDate: string | null;
  readonly labels: CardLabel[];
}

export interface MyCardsResponse {
  readonly data: MyCardItem[];
  readonly pagination: {
    readonly nextCursor: string | null;
  };
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetches cards assigned to the authenticated user with pagination.
 *
 * @param options.cursor - Opaque cursor for fetching the next page
 * @param options.limit - Number of items per page (default 25, max 100)
 */
export async function getMyCards(options?: {
  cursor?: string;
  limit?: number;
}): Promise<MyCardsResponse> {
  const query: Record<string, string> = {};

  if (options?.limit !== undefined) {
    query.limit = String(options.limit);
  }

  if (options?.cursor) {
    query.cursor = options.cursor;
  }

  return api.get<MyCardsResponse>('/v1/users/me/cards', Object.keys(query).length > 0 ? query : undefined);
}
